import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import KimaruLoader from "../components/common/KimaruLoader";
import InventoryFooter from "../components/InventoryFooter";
import { useNavigate, useParams } from "react-router-dom";
import ActionDrawer from "../components/ActionDrawer";
import BadgePill from "../components/BadgePill";

interface AnyMap {
    [key: string]: any;
}

function ActionDrawerPage(props: AnyMap) {
    const { containerId } = useParams<{ containerId?: string }>();
    const [apiData, setApiData] = useState<any>(null);
    const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
    const [processingOption, setProcessingOption] = useState<string>("sap");
    const timerRef = useRef<any>(null);
    const navigate = useNavigate();
    const userEmail = useSelector((state: any) => state.auth?.user?.email) || "monica@kimaru.ai";
    const [containerData, setContainerData] = useState<any>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>("accepted");

    useEffect(() => {
        setContainerData({
            container_id: containerId || "003",
            location: "Asia Pacific > Japan > Miyazaki"
        });
    }, [containerId]);

    useEffect(() => {
        setApiData(null);
        setIsApiLoading(false);
        setError(null);
        setLoadingComplete(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        const fetchData = async () => {
            setIsApiLoading(true);
            try {
                const [movementAcceptedResponse, priceAcceptedResponse, movementDeferredResponse, priceDeferredResponse] = await Promise.all([
                    fetch("https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_accepted_recommendations", { headers: { accept: "application/json", "user-email": userEmail } }),
                    fetch("https://api.stag.kimaru.ai/api/v1/price_recommendation/get_accepted_recommendations", { headers: { accept: "application/json", "user-email": userEmail } }),
                    fetch("https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_defered_recommendations", { headers: { accept: "application/json", "user-email": userEmail } }),
                    fetch("https://api.stag.kimaru.ai/api/v1/price_recommendation/get_defered_recommendations", { headers: { accept: "application/json", "user-email": userEmail } })
                ]);

                const movementAcceptedData: any = movementAcceptedResponse.ok ? await movementAcceptedResponse.json() : { accepted_recommendations: [] };
                const priceAcceptedData: any = priceAcceptedResponse.ok ? await priceAcceptedResponse.json() : { accepted_recommendations: [] };
                const movementDeferredData: any = movementDeferredResponse.ok ? await movementDeferredResponse.json() : { deferred_recommendations: [], total_count: 0 };
                const priceDeferredData: any = priceDeferredResponse.ok ? await priceDeferredResponse.json() : { deferred_recommendations: [], total_count: 0 };

                // Store the full deferred recommendation objects with category
                const movementDeferredArray = Array.isArray(movementDeferredData.deferred_recommendations) 
                    ? movementDeferredData.deferred_recommendations.map((item: any) => ({ ...item, category: 'movement' }))
                    : [];
                
                const priceDeferredArray = Array.isArray(priceDeferredData.deferred_recommendations)
                    ? priceDeferredData.deferred_recommendations.map((item: any) => ({ ...item, category: 'price' }))
                    : [];

                const combinedData: AnyMap = {
                    movement: movementAcceptedData.accepted_recommendations || [],
                    price: priceAcceptedData.accepted_recommendations || [],
                    deferredCount: (movementDeferredData.total_count || 0) + (priceDeferredData.total_count || 0),
                    deferredMovement: movementDeferredArray,
                    deferredPrice: priceDeferredArray
                };

                setApiData(combinedData);
            } catch (err: any) {
                setError(err?.message);
            } finally {
                timerRef.current = setTimeout(() => {
                    setLoadingComplete(true);
                    setIsApiLoading(false);
                }, 600);
            }
        };

        fetchData();

        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [userEmail, containerId]);

    const handleFinish = () => {
        const params = new URLSearchParams({ actions: 'success', type: processingOption, location: containerData?.location || 'Unknown Location', container_id: containerData?.container_id || 'Unknown Container' });
        navigate(`/?${params.toString()}`);
    };

    const handleBack = () => {
        if (containerId) navigate(`/container-recommendations/${containerId}`); else navigate('/');
    };

    (InventoryFooter as any).leftButtonLabel = 'Decide on Recommendations';
    (InventoryFooter as any).rightButtonLabel = 'Finalize Recommendations';

    const processingOptions = [
        { key: 'sap', title: 'Send to S/4 Hana (SAP)', desc: 'kimaru.ai will send the recommendations directly to your ERP', img: '/assets/images/sap.png' },
        { key: 'excel', title: 'Download to Excel', desc: 'Receive a CSV file to import into Excel', img: '/assets/images/excel.png' },
        { key: 'hubspot', title: 'Send to HubSpot', desc: 'Send recommendations to your HubSpot workflows', img: null },
        { key: 'report', title: 'Create Report', desc: 'Generate a report for further analysis', img: null }
    ];

    return (
        <>
            <InventoryFooter isLoading={isApiLoading} currentStep={2} onBackClick={handleBack} onFinishClick={handleFinish} />

            <div className="flex-1 overflow-y-auto pt-0 px-6 pb-4">
                <div className="mt-0 mb-4">
                    <h2 className="text-xl font-semibold">Review Recommendations</h2>
                    <p className="text-sm text-gray-600">Please confirm the recommendation selections you’ve made.</p>
                </div>

                <div className="h-full flex flex-col gap-4">
                    <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden min-h-[360px]">
                        <div className="flex flex-col rounded-l px-2 py-3 w-[23rem] min-h-full bg-white border-r border-gray-200">
                            <div className="flex-grow overflow-y-auto mb-4 px-2">
                                <ul className="space-y-2">
                                    <li className={`rounded ${selectedCategory === 'accepted' ? 'bg-slate-200' : 'hover:bg-gray-50'}`} onClick={() => setSelectedCategory('accepted')}>
                                        <div className="flex items-center justify-between rounded cursor-pointer p-3">
                                            <span className="text-sm text-gray-800">Accepted</span>
                                            <div className="flex items-center gap-2">
                                                <BadgePill count={(apiData?.movement?.filter((it: any) => it.type === 'system_recommendation')?.length || 0) + (apiData?.price?.filter((it: any) => it.strategy !== 'override')?.length || 0)} active={selectedCategory === 'accepted'} />
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12L10 8L6 4" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </div>
                                        </div>
                                    </li>

                                    <li className={`rounded ${selectedCategory === 'adjusted' ? 'bg-slate-200' : 'hover:bg-gray-50'}`} onClick={() => setSelectedCategory('adjusted')}>
                                        <div className="flex items-center justify-between rounded cursor-pointer p-3">
                                            <span className="text-sm text-gray-800">Adjusted</span>
                                            <div className="flex items-center gap-2">
                                                <BadgePill count={(apiData?.movement?.filter((it: any) => it.type === 'override_recommendation')?.length || 0) + (apiData?.price?.filter((it: any) => it.strategy === 'override')?.length || 0)} active={selectedCategory === 'adjusted'} />
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12L10 8L6 4" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </div>
                                        </div>
                                    </li>

                                    <li className={`rounded ${selectedCategory === 'deferred' ? 'bg-slate-200' : 'hover:bg-gray-50'}`} onClick={() => setSelectedCategory('deferred')}>
                                        <div className="flex items-center justify-between rounded cursor-pointer p-3">
                                            <span className="text-sm text-gray-800">Deferred</span>
                                            <div className="flex items-center gap-2">
                                                <BadgePill count={apiData?.deferredCount || 0} active={selectedCategory === 'deferred'} />
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 12L10 8L6 4" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="flex-1 p-6">
                            <ActionDrawer containerData={containerData} apiData={apiData} isApiLoading={isApiLoading} error={error} loadingComplete={loadingComplete} processingOption={processingOption} setProcessingOption={setProcessingOption} onFinish={handleFinish} isLoading={isApiLoading} selectedCategory={selectedCategory} />
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-2">Choose how to process the recommendations</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {processingOptions.map(opt => (
                                <div key={opt.key} className={`flex items-stretch rounded border border-gray-200 bg-white`}>
                                    <div style={{ display: 'flex', width: 55, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, borderRadius: '8px 0 0 8px', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', borderLeft: '1px solid #E2E8F0', background: '#F1F5F9' }}>
                                        <button onClick={() => setProcessingOption(opt.key)} aria-label={`Select ${opt.title}`} style={{ width: 20, height: 20, borderRadius: 999, background: '#FFFFFF', border: '2px solid #E2E8F0', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <div style={{ width: 10, height: 10, borderRadius: 999, background: processingOption === opt.key ? '#0F172A' : 'transparent' }} />
                                        </button>
                                    </div>

                                    <div className="flex-1 px-3 py-2 flex items-center">
                                        <div className="flex-1">
                                            <div className="text-sm font-semibold">{opt.title}</div>
                                            <div className="text-xs text-gray-500">{opt.desc}</div>
                                        </div>

                                        <div className="w-20 h-20 flex items-center justify-center">
                                            {opt.img ? <img src={opt.img} alt={opt.key} style={{ width: 162, height: 112, objectFit: 'contain' }} onError={(e: any) => { const alt = '/assets/images/kimaru-logo/sap.png'; if (e.target.src !== alt) { e.target.src = alt; } else { e.target.style.display = 'none'; console.warn('Image not found', e.target.src); } }} /> : <div style={{ width: 36, height: 36 }} />}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ActionDrawerPage;